import React from 'react'
import styled from 'styled-components'

import { FONT_WEIGHT } from '../../../../styles/theme'
interface ChatMarkdownProps {
  content: string
}

/**
 * Lightweight markdown renderer for chat bubbles.
 * Handles: bold, italic, inline code, headings, numbered/bullet lists.
 * No external dependencies.
 */
export const ChatMarkdown = React.memo(({ content }: ChatMarkdownProps): React.ReactElement => {
  const lines = content.split('\n')
  const elements: React.ReactNode[] = []
  let listItems: { ordered: boolean; text: string }[] = []
  let listOrdered = false

  const flushList = (): void => {
    if (listItems.length === 0) return
    const listKey = `list-${elements.length}`
    const items = listItems.map((item, idx) => <li key={idx}>{renderInline(item.text)}</li>)
    elements.push(listOrdered ? <ol key={listKey}>{items}</ol> : <ul key={listKey}>{items}</ul>)
    listItems = []
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]

    // Headings
    const headingMatch = line.match(/^(#{1,3})\s+(.+)/)
    if (headingMatch) {
      flushList()
      elements.push(<Heading key={i}>{renderInline(headingMatch[2])}</Heading>)
      continue
    }

    // Ordered list: "1. text" or "1) text"
    const olMatch = line.match(/^\d+[.)]\s+(.+)/)
    if (olMatch) {
      if (listItems.length > 0 && !listOrdered) flushList()
      listOrdered = true
      listItems.push({ ordered: true, text: olMatch[1] })
      continue
    }

    // Unordered list: "- text" or "* text"
    const ulMatch = line.match(/^[-*]\s+(.+)/)
    if (ulMatch) {
      if (listItems.length > 0 && listOrdered) flushList()
      listOrdered = false
      listItems.push({ ordered: false, text: ulMatch[1] })
      continue
    }

    // Regular line or blank
    flushList()
    if (line.trim() === '') {
      // Skip consecutive blank lines
      if (elements.length > 0) {
        elements.push(<Spacer key={`sp-${i}`} />)
      }
    } else {
      elements.push(<span key={i}>{renderInline(line)}</span>)
      // Add line break if next line is also a regular line (not a list/heading/blank)
      if (i < lines.length - 1) {
        const next = lines[i + 1]
        if (
          next.trim() !== '' &&
          !next.match(/^(#{1,3})\s/) &&
          !next.match(/^(\d+[.)]\s|[-*]\s)/)
        ) {
          elements.push(<br key={`br-${i}`} />)
        }
      }
    }
  }
  flushList()

  return <MarkdownRoot>{elements}</MarkdownRoot>
})

/** Parse inline markdown: **bold**, *italic*, `code` */
function renderInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = []
  // Match: **bold**, *italic*, `code`
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index))
    }
    if (match[2]) {
      parts.push(<strong key={match.index}>{match[2]}</strong>)
    } else if (match[3]) {
      parts.push(<em key={match.index}>{match[3]}</em>)
    } else if (match[4]) {
      parts.push(<InlineCode key={match.index}>{match[4]}</InlineCode>)
    }
    lastIndex = regex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>
}

const MarkdownRoot = styled.div`
  ol,
  ul {
    margin: 2px 0;
    padding-left: 18px;
  }
  li {
    margin: 1px 0;
  }
`

const Heading = styled.div`
  font-weight: ${FONT_WEIGHT.bold};
  margin: 4px 0 2px;
`

const Spacer = styled.div`
  height: 4px;
`

const InlineCode = styled.code`
  background: rgba(0, 0, 0, 0.08);
  border-radius: 3px;
  padding: 1px 4px;
  font-size: 0.9em;
  font-family: 'SF Mono', 'Fira Code', monospace;
`
